import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowRight, MessageCircle } from "lucide-react";
import Reveal from "./Reveal";

const HERO_BG =
  "https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const WHATS_URL = (msg) =>
  `https://api.whatsapp.com/send?phone=554599744439&text=${encodeURIComponent(msg)}`;

export default function Hero() {
  const { t } = useLang();

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="sap-hero" data-testid="hero-section">
      <div className="sap-hero-media" style={{ backgroundImage: `url(${HERO_BG})` }} />
      <div className="sap-hero-grid" />

      <div className="sap-container relative h-full min-h-[92vh] flex items-center pt-28 pb-20">
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <span className="sap-divider-gold" />
              <span
                className="text-[11px] uppercase font-medium tracking-[0.32em]"
                style={{ color: "var(--sap-gold)" }}
              >
                {t.hero.overline}
              </span>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <h1
              className="font-serif text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(2.4rem, 5vw, 4.5rem)", fontWeight: 400 }}
            >
              {t.hero.title}
            </h1>
          </Reveal>

          <Reveal delay={220}>
            <p className="mt-8 text-lg md:text-xl leading-relaxed text-white/80 max-w-2xl font-light">
              {t.hero.subtitle}
            </p>
          </Reveal>

          <Reveal delay={340}>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                type="button"
                data-testid="hero-primary-cta"
                onClick={() => scrollTo("diagnostic")}
                className="sap-btn-primary"
              >
                {t.hero.primary_cta}
                <ArrowRight size={16} strokeWidth={1.75} />
              </button>
              <a
                data-testid="hero-whatsapp-cta"
                href={WHATS_URL(t.whatsapp_msg)}
                target="_blank"
                rel="noopener noreferrer"
                className="sap-btn-ghost-invert"
              >
                <MessageCircle size={16} strokeWidth={1.75} />
                {t.hero.secondary_cta}
              </a>
            </div>
          </Reveal>

          <Reveal delay={480}>
            <div className="mt-16 grid grid-cols-3 gap-6 max-w-lg border-t border-white/15 pt-8">
              {[t.hero.metric_1, t.hero.metric_2, t.hero.metric_3].map((m, i) => (
                <div key={i} data-testid={`hero-metric-${i}`}>
                  <div
                    className="font-serif text-3xl md:text-4xl"
                    style={{ color: "var(--sap-gold)" }}
                  >
                    {m.value}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-white/70 mt-2">
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      {/* subtle bottom fade */}
      <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-b from-transparent to-white/5" />
    </section>
  );
}
